import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';

export default function StoreProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const fetchCatalog = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.get('/api/v1/admin/store/products', { headers });
      if (res.data.success) setProducts(res.data.data);
    } catch (err) {
      setError('Failed to fetch store products.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalog();
  }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`/api/v1/admin/store/products/${id}`, { headers });
      fetchCatalog();
    } catch (err) {
      alert('Failed to delete product');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingBagIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight="bold">Store & Merchandise Products</Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/store-products/new')}
          sx={{ borderRadius: 2, px: 2.5 }}
        >
          Add New Product
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#F8FAFC' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Product Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Store Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sale Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No products found. Click "+ Add New Product" to create your first product!
                  </TableCell>
                </TableRow>
              ) : (
                products.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <img
                          src={p.images?.[0] || 'https://images.unsplash.com/photo-1521572267360-ee0c2909d518?w=100'}
                          alt={p.name}
                          style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover', background: '#F1F5F9' }}
                        />
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">{p.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{p.slug}</Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{p.category?.name || 'General'}</Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={p.storeType === 'camper_store' ? "Camper's Store" : p.storeType === 'both' ? 'Both' : 'Merchandise'}
                        size="small"
                        color={p.storeType === 'camper_store' ? 'warning' : 'primary'}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={p.saleType.toUpperCase()}
                        size="small"
                        color={p.saleType === 'rental' ? 'success' : 'default'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>

                    <TableCell>
                      {p.saleType === 'rental' && (
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          ₹{Number(p.baseRentalPrice).toLocaleString('en-IN')} <span style={{ fontSize: '0.75rem', color: '#64748B' }}>/ trek</span>
                        </Typography>
                      )}
                      {p.saleType === 'purchase' && (
                        <Typography variant="body2" fontWeight="bold">
                          ₹{Number(p.basePurchasePrice).toLocaleString('en-IN')}
                        </Typography>
                      )}
                      {p.saleType === 'both' && (
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            Rent: ₹{Number(p.baseRentalPrice).toLocaleString('en-IN')} / trek
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Buy: ₹{Number(p.basePurchasePrice).toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                        <Tooltip title="Edit Product">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/store-products/edit/${p.id}`)}
                            sx={{ bgcolor: '#F1F5F9' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Product">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(p.id, p.name)}
                            sx={{ bgcolor: '#FEE2E2' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
